import React, { memo, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'

import { Button, Icon, Alert } from '../../../components/common/index'
import { LanguageContext } from '../../../context/LanguageContext'
import { util } from '../../../util'

const Stepper = ({ activeStep, children }) => {
    return (
        <div className={'stepper'}>
            {React.Children.toArray(children).map((child, i) =>
                React.cloneElement(child, {
                    active_step: activeStep,
                    step: i,
                    isopen: activeStep === i, // used to resist re-rendering.
                })
            )}
        </div>
    )
}

Stepper.propTypes = {
    /** @param activeStep {Number} current active step among all content. */
    activeStep: PropTypes.number,

    /** @param children {JSX} an array of Step component jsx objects */
    children: PropTypes.arrayOf(PropTypes.node),
}

/**
 *Step component will create the section layout along with step number and it state(active, partial, disabled)
 */
const Step = ({ step, active_step, children, isPartial, isEditingCompletedPromo }) => {
    const { rtl } = useContext(LanguageContext)
    const isOpen = active_step === step

    const progressClass = util.joinClasses(
        'progress__step',
        !isOpen && 'close',
        isPartial && !isOpen && 'partial'
    )

    const renderStepState = () => {
        if (isEditingCompletedPromo && step === 4) {
            return <Icon size={'md'} color={'danger'} renderIcon={util.ReportProblemIcon} />
        } else if (!isPartial || isOpen) {
            return <div className={progressClass}>{step + 1}</div>
        } else {
            return (
                <Icon
                    className={'progress__check-icon'}
                    renderIcon={util.CheckCircleRoundedIcon}
                    color={'success'}
                />
            )
        }
    }

    return (
        <div className={'stepper__step'}>
            <div className={'progress'}>
                {renderStepState()}
                <div
                    className={util.joinClasses('progress__bar', rtl && 'rtl', step >= 4 && 'last')}
                />
            </div>
            <div className={'step-container'}>
                {React.Children.toArray(children).map(child =>
                    React.cloneElement(child, {
                        step: step,
                        active_step: active_step,
                    })
                )}
            </div>
        </div>
    )
}

Step.propTypes = {
    /** @param step {Number} step value received from Stepper component */
    step: PropTypes.number,

    /** @param active_step {Number} active step among all content */
    active_step: PropTypes.number,

    /** @param children {JSX} All the components (Heading, Content, Actions) wrapped in between Step */
    children: PropTypes.arrayOf(PropTypes.node),

    /** @param isPartial {Boolean} determines whether the sections is configured or not. */
    isPartial: PropTypes.bool,

    /** @param isEditingCompletedPromo {Boolean} if true then we have to render warning icon instead of step number. */
    isEditingCompletedPromo: PropTypes.bool,
}

/**
 *
 * @param step {Number} step value received from Stepper component
 * @param active_step {Number} active step among all content
 * @param children {JSX} Section Heading
 * @param isPartial {Boolean} determines whether the sections is configured or not.
 * @returns {JSX.Element}
 */
const Header = ({ children }) => {
    return <div className={`step-container__heading`}>{children}</div>
}

Header.propTypes = {
    children: PropTypes.string,
}

/**
 * Content component will determine whether to display a partial content, no content or content
 */
const Content = ({
    renderPartialContent,
    children,
    showAlert,
    isPartial,
    editHandler,
    active_step,
    step,
}) => {
    const { strings } = useContext(LanguageContext)

    /* this useEffect is to scroll the scroll bar to current step section */
    useEffect(() => {
        if (step === active_step) {
            window.scrollTo({
                top: active_step * 270,
                behavior: 'smooth',
            })
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active_step])

    const renderPartialView = () => (
        <div className={'step-container__children partial'}>
            <div className={'partial-content'}>
                <div className={'partial-content__body'}>{renderPartialContent(step)}</div>
                <div className={'partial-content__editable'} onClick={() => editHandler(step)}>
                    <p className={'paragraph px-1'}>{strings.OPEN}</p>
                    <Icon size={'xs'} renderIcon={util.ExpandMoreIcon} />
                </div>
            </div>
        </div>
    )

    const renderStepperChildren = () => (
        <div className={'step-container__children'}>
            <Alert
                show={showAlert}
                className={'failed-alert mb-7'}
                title={strings.updatePriorityFailedMsg}
                type={'danger'}
                variant={'fill'}
                renderIcon={util.ReportProblemIcon}
            />
            {children}
        </div>
    )

    /* if active step is not current step, but if that sections data had been already configured
    then render partial/closed view of that section */
    if (step !== active_step && isPartial) {
        return renderPartialView()
    } else if (step !== active_step) {
        /* if active step is not equal to current step and if that section's data had never been
        configured before then render no children */
        return null
    } else {
        /* if active step is equal to current step then render section */
        return renderStepperChildren()
    }
}

Content.propTypes = {
    /** @param step {Number} step value received from Stepper component */
    step: PropTypes.number,

    /** @param active_step {Number} active step among all content */
    active_step: PropTypes.number,

    /** @param children {JSX} Different sections body (placements, schedule, audience, manage content, prioritization) */
    children: PropTypes.node,

    /** @param renderPartialContent {callback} renders the partial content of the sections */
    renderPartialContent: PropTypes.func,

    /** @param editHandler {callback} changes the step and opens the component. */
    editHandler: PropTypes.func,

    /** @param isPartial {Boolean} determines whether the sections is configured or not. */
    isPartial: PropTypes.bool,

    /** @param showAlert {Boolean} */
}

/**
 * Actions component renders the save and cancel buttons.
 */
const Actions = ({ saveClickHandler, cancelClickHandler, isEnable, isEditing, step }) => {
    const { strings } = useContext(LanguageContext)
    return (
        <div className={'step-container__actions'}>
            <Button onClick={saveClickHandler} color={'primary'} size={'lg'} disabled={isEnable}>
                {step !== 5 ? strings.SAVE_CONTINUE : strings.SAVE}
            </Button>
            {isEditing && (
                <Button
                    onClick={cancelClickHandler}
                    color={'light'}
                    size={'lg'}
                    className={'ml-3 mr-3'}
                >
                    {strings.CANCEL}
                </Button>
            )}
        </div>
    )
}

Actions.propTypes = {
    /** @param saveClickHandler {callback} post the configuration to resource server. */
    saveClickHandler: PropTypes.func,

    /** @param cancelClickHandler {callback} closes the current content. */
    cancelClickHandler: PropTypes.func,

    /** @param isEnable {Boolean} determines whether to display an active or inactive save button */
    isEnable: PropTypes.bool,
    isEditing: PropTypes.bool,
    isPublished: PropTypes.bool,
    step: PropTypes.number,
}

Stepper.Step = memo(Step)
Stepper.Header = memo(Header, () => true)
Stepper.Content = memo(Content)
Stepper.Actions = memo(Actions)

export default Stepper
